const PLAUSIBLE_SCRIPT_SRC = import.meta.env.VITE_PLAUSIBLE_SRC ?? "https://plausible.io/js/script.js";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    plausible?: (event: string, options?: { props?: Record<string, unknown> }) => void;
  }
}

let analyticsInitialized = false;

const injectScript = (id: string, build: () => HTMLScriptElement) => {
  if (typeof document === "undefined") {
    return;
  }

  if (document.head.querySelector(`script[data-analytics-id="${id}"]`)) {
    return;
  }

  const script = build();
  script.setAttribute("data-analytics-id", id);
  document.head.appendChild(script);
};

const initPlausible = () => {
  const domain = import.meta.env.VITE_PLAUSIBLE_DOMAIN;
  if (!domain) {
    return;
  }

  injectScript(`plausible-${domain}`, () => {
    const script = document.createElement("script");
    script.defer = true;
    script.dataset.domain = domain;
    script.src = PLAUSIBLE_SCRIPT_SRC;
    return script;
  });
};

const initGoogleAnalytics = () => {
  const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;
  if (!measurementId) {
    return;
  }

  injectScript(`ga-${measurementId}-loader`, () => {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    return script;
  });

  injectScript(`ga-${measurementId}-config`, () => {
    const script = document.createElement("script");
    script.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){window.dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${measurementId}');
    `;
    return script;
  });
};

const canRunAnalytics = () => {
  if (import.meta.env.PROD) {
    return true;
  }
  return import.meta.env.VITE_ENABLE_ANALYTICS_IN_DEV === "true";
};

export const enableAnalytics = () => {
  if (typeof window === "undefined") {
    return;
  }

  if (analyticsInitialized) {
    return;
  }

  if (!canRunAnalytics()) {
    return;
  }

  analyticsInitialized = true;
  initPlausible();
  initGoogleAnalytics();
};

export const isAnalyticsEnabled = () => analyticsInitialized;

export const trackEvent = (eventName: string, params: Record<string, unknown> = {}) => {
  if (typeof window === "undefined") {
    return;
  }

  window.gtag?.("event", eventName, params);
  window.plausible?.(eventName, { props: params });
};

